
























import dayjs from 'dayjs';
import { Vue, Component, Prop, Emit, Watch } from 'vue-property-decorator';

/**
 * Buefy b-datetimepicker is just a combination of two separate components:
 * - b-datepicker
 * - b-timepicker
 *
 * Therefore, it shares props and events from the two components. In order
 * to programmatically access and modify the props on render, create local
 * interfaces for b-timepicker and declare local values for
 * desired props.
 */
interface BTimepicker {
  // Configure time selection to increment value
  incrementMinutes: number;
}

@Component({})
export default class DateTimeFieldComponent extends Vue {
  @Prop({ default: false }) hasFieldErrorMessage!: boolean;
  @Prop({ type: Date, default: null })
  readonly value!: Date | null;
  @Prop() minDate!: Date | null;
  @Prop() maxDate!: Date | null;

  // Duplicate value to prevent directly mutating the prop
  internalDateValue: Date | null = null;
  timepickerProps: BTimepicker = {
    incrementMinutes: 15 // 15 minute increments for selection
  };

  /**
   * Date time fields can share the same date, but the end time must be at least
   * 15 minutes after the start time (and vice-versa).
   */
  // The minimum end date should be 15 minutes AFTER the selected start date.
  get trueMinDate() {
    if (!this.minDate) {
      return null;
    }

    return dayjs(this.minDate).add(15, 'minute').toDate();
  }

  // The maximum start date should be 15 minutes BEFORE the end date.
  get trueMaxDate() {
    if (!this.maxDate) {
      return null;
    }

    return dayjs(this.maxDate).subtract(15, 'minute').toDate();
  }

  formatDate(date: Date) {
    if (!date || !dayjs(date).isValid()) {
      return '';
    }

    return dayjs(date.toISOString()).format('DD MMM YYYY h:mmA');
  }

  clearDate() {
    this.internalDateValue = null;
  }

  currentDate() {
    this.internalDateValue = dayjs(new Date()).toDate();
  }

  // Watch the 'value' prop to sync changes FROM the parent TO the child.
  @Watch('value', { immediate: true })
  onValueChange(newValue: Date | null): void {
    // Update internal state only if it's different, to prevent infinite loops.
    if (newValue !== this.internalDateValue) {
      this.internalDateValue = newValue;
    }
  }

  // Watch the internal state to sync changes FROM the child TO the parent.
  @Watch('internalDateValue')
  onInternalValueChange(newValue: Date | null): void {
    // Emit the 'input' event, which v-model on the parent listens for.
    this.emitInputToParent(newValue);
  }

  // Emit 'input' event for parent binding
  @Emit('input')
  emitInputToParent(newValue: Date | null) {
    return newValue;
  }
}
